import React, { useRef, useState, useEffect } from 'react';

const MultiVideoPlayer = ({ videos, onPlayerReady }) => {
  const mainRef = useRef(null);
  const previewRef = useRef(null);

  const [durations, setDurations] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [hoverData, setHoverData] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load durations
  useEffect(() => {
    const loadDurations = async () => {
      setLoading(true);
      const result = await Promise.all(
        videos?.map(
          (src) =>
            new Promise((resolve) => {
              const vid = document.createElement('video');
              vid.src = src;
              vid.preload = 'metadata';
              vid.onloadedmetadata = () => resolve(vid.duration || 0);
              vid.onerror = () => resolve(0);
            }),
        ),
      );
      setDurations(result);
      setLoading(false);
    };
    if(videos){
      loadDurations();
    }
  }, [videos]);

  const totalDuration = durations.reduce((a, b) => a + b, 0);

  // Find video index and local time from global time
  const findVideoAtTime = (globalTime) => {
    let timeLeft = globalTime;
    for (let i = 0; i < durations.length; i++) {
      if (timeLeft <= durations[i]) return { index: i, time: timeLeft };
      timeLeft -= durations[i];
    }
    return { index: durations.length - 1, time: durations.at(-1) || 0 };
  };

  // Get global time from current video position
  const getGlobalTime = () => {
    let elapsed = 0;
    for (let i = 0; i < currentIndex; i++) elapsed += durations[i] || 0;
    elapsed += currentTime;
    return elapsed;
  };

  // Format time helper
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Expose player controls to parent component
  useEffect(() => {
    if (onPlayerReady && mainRef.current) {
      const controls = {
        play: () => mainRef.current?.play().catch(console.error),
        pause: () => mainRef.current?.pause(),
        getCurrentTime: () => mainRef.current?.currentTime || 0,
        getCurrentIndex: () => currentIndex,
        isPlaying: () => isPlaying,
        seek: (time) => {
          if (mainRef.current) mainRef.current.currentTime = time;
        },
        goToVideo: (index) => loadVideo(index, 0),
      };
      onPlayerReady(controls);
    }
  }, [onPlayerReady, isPlaying, currentIndex, videos?.length]);

  // ---------- NEW: loadVideo function ----------
  const loadVideo = (index, time = 0) => {
    if (!mainRef.current || !videos?.[index]) return;
    const video = mainRef.current;

    // Cancel any ongoing playPromise
    if (video.playPromise) {
      video.playPromise.catch(() => {});
      video.playPromise = null;
    }

    video.pause();
    video.removeAttribute('src');
    video.load();

    video.src = videos?.[index];
    video.currentTime = time;

    const playVideo = () => {
      video.playPromise = video
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    };

    if (video.readyState >= 3) {
      playVideo();
    } else {
      video.addEventListener('canplay', playVideo, { once: true });
    }

    setCurrentIndex(index);
    setCurrentTime(time);
  };

  // Initialize first video
  useEffect(() => {
    if (!loading && videos?.length > 0) loadVideo(0, 0);
  }, [loading, videos]);

  // Handle hover
  const handleHover = (e) => {
    if (!totalDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const globalTime = percent * totalDuration;
    const { index, time } = findVideoAtTime(globalTime);

    if (previewRef.current && videos?.[index]) {
      previewRef.current.src = videos?.[index];
      previewRef.current.currentTime = time;
      previewRef.current.volume = 0;
      previewRef.current.muted = true;
      previewRef.current.play().catch(() => {});
    }

    setHoverData({
      x: Math.max(80, Math.min(e.currentTarget.offsetWidth - 80, e.clientX - rect.left)),
      index,
      time: globalTime,
    });
  };

  // Handle click
  const handleClick = (e) => {
    if (!totalDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const globalTime = percent * totalDuration;
    const { index, time } = findVideoAtTime(globalTime);

    loadVideo(index, time);
  };

  // Update progress
  useEffect(() => {
    const video = mainRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      let elapsed = 0;
      for (let i = 0; i < currentIndex; i++) elapsed += durations[i] || 0;
      elapsed += video.currentTime;
      setProgress(elapsed / totalDuration);
    };

    const handleEnded = () => {
      if (currentIndex < videos?.length - 1) {
        loadVideo(currentIndex + 1, 0);
      } else {
        setIsPlaying(false);
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
    };
  }, [currentIndex, durations, videos]);

  // Handle seeking
  useEffect(() => {
    const video = mainRef.current;
    if (video && Math.abs(video.currentTime - currentTime) > 0.5) {
      video.currentTime = currentTime;
    }
  }, [currentTime]);

  // Timeline helpers
  const createVideoSegments = () => {
    const segments = [];
    let cumulativeTime = 0;
    durations.forEach((duration, i) => {
      const startPercent = (cumulativeTime / totalDuration) * 100;
      const widthPercent = (duration / totalDuration) * 100;
      segments.push(
        <div
          key={`segment-${i}`}
          className={`absolute top-0 h-full transition-opacity ${i === currentIndex ? 'bg-blue-100' : 'bg-gray-200'}`}
          style={{ left: `${startPercent}%`, width: `${widthPercent}%` }}
        />,
      );
      cumulativeTime += duration;
    });
    return segments;
  };

  const createVideoSeparators = () => {
    const separators = [];
    let cumulativeTime = 0;
    for (let i = 0; i < durations.length - 1; i++) {
      cumulativeTime += durations[i];
      const leftPercent = (cumulativeTime / totalDuration) * 100;
      separators.push(
        <div
          key={`separator-${i}`}
          className="absolute top-0 bottom-0 w-[3px] bg-white shadow-sm rounded-full"
          style={{ left: `${leftPercent}%` }}
        />,
      );
    }
    return separators;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center gap-6">
        <div className="w-2/3 h-64 bg-gray-200 rounded-lg flex items-center justify-center">
          <p className="text-gray-500">Loading videos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 p-4">
      {/* Main player */}
      <video
        ref={mainRef}
        className="w-[90%] h-[20rem] rounded-lg shadow-lg"
        style={{ transform: "scaleX(-1)" }}
        controls
        playsInline
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      {/* Combined timeline */}
      <div className="w-[90%]">
        <div
          className="relative w-full h-3 bg-gray-300 rounded-full cursor-pointer shadow-inner"
          onMouseMove={handleHover}
          onMouseLeave={() => {
            setHoverData(null);
            previewRef.current?.pause();
          }}
          onClick={handleClick}
        >
          {createVideoSegments()}
          <div
            className="absolute top-0 left-0 h-full rounded-full transition-all duration-100"
            style={{ width: `${progress * 100}%`, background: 'linear-gradient(to right, #0a7f8f, #0a7f8f)' }}
          />
          {createVideoSeparators()}
          <div
            className="absolute top-[-2px] w-4 h-5 rounded-full shadow-lg transform -translate-x-1/2 transition-all duration-100"
            style={{ left: `${progress * 100}%`, backgroundColor: '#0a7f8f' }}
          />

          {hoverData && (
            <div className="absolute -top-32 w-48 bg-black rounded-lg shadow-xl pointer-events-none z-10" style={{ left: hoverData.x - 96 }}>
              <video ref={previewRef} muted playsInline preload="metadata" className="w-full h-28 object-cover rounded-t-lg" />
              <div className="p-2 text-center">
                <p className="text-xs text-white font-medium">Video {hoverData.index + 1}</p>
                <p className="text-xs text-gray-300">{formatTime(hoverData.time)}</p>
              </div>
            </div>
          )}
        </div>
        <p className="text-gray-600">{formatTime(getGlobalTime())} / {formatTime(totalDuration)}</p>
      </div>
    </div>
  );
};

export default MultiVideoPlayer;
